import React, { useMemo } from "react"
import { Select } from "antd"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  XTAB_STATISTIC_OPTIONS,
  DTAB_STATISTIC_OPTIONS,
} from "shared/ui/Builder"
import {
  getBoolChildLabels,
  flattenCodebookForSelect,
} from "utils/codebookUtils"
import { DataTile } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import { repeatOf } from "features/Dashboard/runtime/panels"
import {
  effectiveStatistic,
  viewStatistic,
} from "features/Dashboard/runtime/tileView"

/**
 * The controls a builder tile exposes to viewers: the statistic, the
 * breakdown (first column variable) and the categories of the first row
 * variable. Choices are viewer state (`setTileControl`), not document edits.
 * A repeated tile shows none, so every panel runs the same table.
 */
const TileControls: React.FC<{ tile: DataTile }> = ({ tile }) => {
  const codebook = useDashboardStore((s) => s.codebook)
  const control = useDashboardStore((s) => s.tileControls[tile.id])
  const setTileControl = useDashboardStore((s) => s.setTileControl)
  const firstRow =
    tile.source.kind === "builder" ? tile.source.rows[0] : undefined
  const leafOptions = useMemo(
    () =>
      (firstRow ? getBoolChildLabels(codebook, firstRow) : []).map((l) => ({
        value: l,
        label: l,
      })),
    [codebook, firstRow]
  )
  const variables = useMemo(
    () => flattenCodebookForSelect(codebook ?? []),
    [codebook]
  )
  if (tile.source.kind !== "builder" || repeatOf(tile)) return null
  const { source, controls } = tile
  const stats =
    source.operation === TABLE_OPERATION_TYPE.DTAB
      ? DTAB_STATISTIC_OPTIONS
      : XTAB_STATISTIC_OPTIONS
  // What the tile draws: a chart or number draws one statistic; a table or
  // scorecard names the statistic its view is built in (see
  // `viewStatistic`) until the viewer picks one. A builder tile's is always
  // one of its statistics.
  const shownStatistic =
    tile.type === "table" || tile.type === "scorecard"
      ? (viewStatistic(tile, control) as STATISTIC_TYPE)
      : effectiveStatistic(tile, control)
  return (
    <>
      {controls.statistic && (
        <Select
          size="small"
          aria-label="Statistic"
          value={shownStatistic}
          options={stats}
          style={{ minWidth: 110 }}
          onChange={(v) => setTileControl(tile.id, { statistic: v })}
        />
      )}
      {controls.breakdown && (
        <Select
          size="small"
          aria-label="Breakdown"
          showSearch
          allowClear
          placeholder="Breakdown"
          value={control?.breakdown ?? source.columns[0]}
          options={variables}
          style={{ minWidth: 140 }}
          onChange={(v) =>
            setTileControl(tile.id, { breakdown: v ?? undefined })
          }
        />
      )}
      {/* applyControls keeps categories for a single row variable only. */}
      {controls.categories &&
        source.rows.length === 1 &&
        leafOptions.length > 0 && (
        <Select
          size="small"
          aria-label="Categories"
          mode="multiple"
          placeholder="All categories"
          maxTagCount="responsive"
          value={control?.categories ?? []}
          options={leafOptions}
          style={{ minWidth: 160 }}
          onChange={(v) => setTileControl(tile.id, { categories: v })}
        />
      )}
    </>
  )
}

export default TileControls
